<template>
  <div class="c-video">
    <video
      :id="videoElementId"
      ref="video"
      class="c-video__video"
      :src="html5Src"
      :poster="poster"
      :controls="useControls"
      @canplay="updatePaused($event.target.paused)"
      @playing="updatePaused($event.target.paused)"
      @pause="updatePaused($event.target.paused)"
      @click="handleVideo"
    >
      <a
        :href="cdnSrc"
        target="_blank"
      >
        If your browser does not support the video tag, download the video here.
      </a>
    </video>
    <button
      v-if="!useControls && paused"
      v-show="paused"
      class="c-video__play-button"
      @click="handleVideo"
    >
      <img
        class="c-video__play-icon"
        src="@/assets/icons/play.svg"
        :alt="(paused) ? 'play' : 'pause'"
      >
    </button>
  </div>
</template>

<script>
import Hls from 'hls.js'
import { UrlFilters } from '@mixins/cdnMixins'
export default {
  name: "Vid",
  mixins: [ UrlFilters ],
  props: {
    src: {
      type: String,
      default: ''
    },
    poster: {
      type: String,
      default: null
    },
    type: {
      type: String,
      default: 'video/mp4'
    },
    useControls: {
      type: Boolean,
      default: true,
    }
  },
  data() {
    return {
      playbackStrategy: null,
      paused: true
    }
  },
  computed: {
    cdnSrc() {
      return this.$getCacheUrl(this.src)
    },
    videoElementId() {
      return `j-video-${this._uid}`
    },
    html5Src() {
      const playbackStrategy = this.playbackStrategy
      const src = this.cdnSrc
      return (playbackStrategy === 'html5') ? src : null
    },
    videoType() {
      const type = this.type
      const srcUrl = this.cdnSrc
      let result = 'video/mp4'
      if (srcUrl.endsWith('.m3u8')) {
        result = 'application/vnd.apple.mpegurl'
      } else if (type.includes('/')) {
        result = type
      }
      return result
    }
  },
  watch: {
    src(src) {
      if (src) {
        this.setupSrc()
      }
    },
  },
  mounted() {
    if (this.src) {
      this.setupSrc()
    }
  },
  methods: {
    updatePaused(value) {
      this.paused = value
    },
    async setupHtml5(video) {
      this.playbackStrategy = 'html5'
      await new Promise((resolve) => {
        const handler = () => {
          video.removeEventListener('loadedmetadata', handler)
          resolve()
        }
        video.addEventListener('loadedmetadata', handler)
      })
    },
    async setupHls(video, src) {
      this.playbackStrategy = 'hls'
      this.hls = new Hls()
      this.hls.loadSource(src)
      this.hls.attachMedia(video)
      await new Promise((resolve) => {
        const handler = () => {
          this.hls.off(Hls.Events.MANIFEST_PARSED, handler)
          resolve()
        }
        this.hls.on(Hls.Events.MANIFEST_PARSED, handler)
      })
    },
    async setupSrc() {
      const video = this.$refs.video
      const src = this.cdnSrc
      const type = this.videoType
      if (
        video.canPlayType(type) &&
        !video.src
      ) {
        await this.setupHtml5(video)
      } else if (
        Hls.isSupported() &&
        !this.hls &&
        !video.src
      ) {
        await this.setupHls(video, src)
      } else if (!this.hls && !video.src) {
        console.error('[Vid] video type is not supported', type, src)
      }
    },
    async handleVideo() {
      try {
        const video = this.$refs.video
        if (this.paused && video) {
          video.play()
        } else if (video) {
          video.pause()
        }
      } catch (err) {
        console.warn(err)
      }
    },
  },
}
</script>

<style lang="scss" scoped>
.c-video {
  position: relative;
  display: inline-block;
  cursor: pointer;

  &__video {
    max-width: 100%;
  }

  &__play-button {
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    border-radius: 100%;
    overflow: hidden;
    width: rem(100px);
    height: rem(100px);
    border: none;
    @include themify($themes) {
      background-color: transparentize(themed('background', 'dark'), 0.4);
    }
  }

  &__play-icon {
    border-radius: 100%;
    max-width: 100%;
  }
}
</style>
